import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from "react";

type LocationState = {
  pathname: string;
  search: string;
};

type RouterValue = {
  location: LocationState;
  navigate: (to: string, options?: { replace?: boolean }) => void;
};

const RouterContext = createContext<RouterValue | null>(null);

function readLocation(): LocationState {
  return {
    pathname: window.location.pathname,
    search: window.location.search,
  };
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState(readLocation);

  useEffect(() => {
    const syncLocation = () => setLocation(readLocation());
    window.addEventListener("popstate", syncLocation);
    return () => window.removeEventListener("popstate", syncLocation);
  }, []);

  const value = useMemo<RouterValue>(
    () => ({
      location,
      navigate: (to, options) => {
        const next = new URL(to, window.location.origin);
        const href = `${next.pathname}${next.search}${next.hash}`;
        window.history[options?.replace ? "replaceState" : "pushState"]({}, "", href);
        setLocation(readLocation());
        window.scrollTo({ top: 0, behavior: "instant" });
      },
    }),
    [location],
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

function useRouter() {
  const router = useContext(RouterContext);
  if (!router) throw new Error("Router hooks must be used within RouterProvider.");
  return router;
}

export function useLocation() {
  return useRouter().location;
}

export function useNavigate() {
  return useRouter().navigate;
}

export function useSearchParams(): [URLSearchParams] {
  const { search } = useLocation();
  return useMemo(() => [new URLSearchParams(search)], [search]);
}

export function useParams(): { replayId?: string } {
  const { pathname } = useLocation();
  const match = pathname.match(/^\/replays\/([^/]+)\/?$/);
  return { replayId: match ? decodeURIComponent(match[1]!) : undefined };
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  to: string;
};

export function Link({ to, onClick, ...props }: LinkProps) {
  const navigate = useNavigate();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      props.target === "_blank"
    ) {
      return;
    }
    event.preventDefault();
    navigate(to);
  };

  return <a {...props} href={to} onClick={handleClick} />;
}

type NavLinkProps = Omit<LinkProps, "className"> & {
  className?: string | ((state: { isActive: boolean }) => string);
  end?: boolean;
};

export function NavLink({ className, end = false, to, ...props }: NavLinkProps) {
  const { pathname } = useLocation();
  const targetPath = new URL(to, window.location.origin).pathname;
  const isActive = end
    ? pathname === targetPath
    : pathname === targetPath || pathname.startsWith(`${targetPath}/`);
  const resolvedClassName =
    typeof className === "function" ? className({ isActive }) : className;

  return (
    <Link
      {...props}
      to={to}
      className={resolvedClassName}
      aria-current={isActive ? "page" : undefined}
    />
  );
}
